import { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { Button } from '@upmc/ims.component-library';
import Unknown from '../../components/AppCart/GenericApps/Unknown/Unknown';
import Submitted from '../../components/AppCart/GenericApps/Submitted/Submitted';
import Chip from '../../components/UI/Chips/Chip/Chip';
import GenericForm from '../../components/UI/GenericForm/GenericForm';
import Icon from '../../components/UI/Icon/Icon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import { Headline4 } from '../../components/UI/MDC/Typography/Typography';
import { RequestTypes } from '../../constants/enums';
import * as RequestConstants from '../../constants/requestConstants';
import AppContainer from '../../containers/AppContainer/AppContainer';
import StyleAuxiliary from '../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import WithLoadingHandler from '../../hoc/withLoadingHandler/withLoadingHandler';
import axiosInstance from '../../services/axios/axios';
import * as appsActions from '../../store/apps/actions';
import * as ancillaryActions from '../../store/ancillary/actions';
import { getSession } from '../../store/utility';
import { getLegacyPath } from 'routes/_utils';
import { selectModifyQueue } from '../../store/uar/selectors';
import AnnualReviewActionContainer from '../AnnualUserAccessReview/containers/AnnualReviewActionContainer/AnnualReviewActionContainer';

import AppForm from './components/AppForm';

const UAR_REVIEW_ROUTE = '/UserAccessReview/Review';

const getAnnualReviewQueueProgressionPath = trackingNumber => {
	return getLegacyPath(`/Confirm/Index?tn=${trackingNumber}`);
};

/**
 * AppRequest class component:  primary mounting point for index.js
 */
class AppRequest extends Component {
	constructor(props) {
		super();
		this.state = {
			tabApps: [],
			receivedApps: null,
			redirectURL: '',
			pageLocation: 'home',
			isDialogOpen: false,
			isSnackbarOpen: false,
			uarFlowBannerText: '',
			sNextQueueItem: null,
		};
	}

	/**
	 * handleActiveIndexUpdate()
	 * TabBar.props.handleActiveIndexUpdate handler
	 * @param activeIndex index of requested tab
	 */
	handleActiveIndexUpdate = activeIndex => {
		this.props.updateIndex(activeIndex);
	};

	/**
	 * compnentDidUpdate() lifecycle method
	 * @param {object} prevProps - represents the previous props state
	 */
	componentDidUpdate(prevProps) {
		if (this.props.appsData !== prevProps.appsData) {
			let tabApps = [];

			for (let app in this.props.appsData) {
				const el = {
					id: this.props.appsData[app].id,
					name: this.props.appsData[app].Name,
				};
				tabApps.push(el);
			}
			this.setState({
				tabApps: tabApps,
			});

			// reset active tab in response to request change
			this.props.updateIndex(
				this.props.activeIndex - 1 > 0 ? this.props.activeIndex - 1 : 0
			);

			if (tabApps.length === 0) {
				const query = new URLSearchParams(window.location.search);
				const isAnnualReviewActionFlow =
					query.get('uarSource') === 'annualReview';
				const isRequestDataLoaded =
					this.state.receivedApps !== null && !this.props.loading;

				if (isAnnualReviewActionFlow) {
					if (isRequestDataLoaded) {
						this.setState({ redirectURL: UAR_REVIEW_ROUTE });
					}
				} else {
					this.setState({ isSnackbarOpen: true });
				}
			}
		}
	}

	/**
	 * componentDidMount() lifecycle method
	 * check for browser and display messages for unsupported
	 * retrieve the Tracking Number from the query params
	 */
	async componentDidMount() {
		const query = new URLSearchParams(window.location.search);
		const tn = query.get(RequestConstants.TRACKING_NUMBER);
		const requestType = query.get(RequestConstants.REQUEST_TYPE);
		const uarSource = query.get('uarSource');
		const uarTargetName = query.get('uarTargetName');
		const uarStep = query.get('uarStep');
		const uarTotal = query.get('uarTotal');
		let esos = [];

		if (uarSource === 'annualReview' && uarTargetName) {
			let breadcrumbText = `${uarTargetName}`;
			if (uarStep && uarTotal) {
				breadcrumbText += ` (${uarStep} of ${uarTotal})`;
			}

			if (this.props.modifyQueue && this.props.modifyQueue.length > 0) {
				const currentStep = Math.max(1, Number(uarStep || 1));
				const nextQueueItem = this.props.modifyQueue[currentStep];

				if (nextQueueItem) {
					this.setState({
						sNextQueueItem: nextQueueItem,
					});
				}
			}

			this.setState({ uarFlowBannerText: breadcrumbText });
		}

		if (requestType !== null && this.props.requestType !== requestType) {
			this.props.updateRequestType(requestType);
		} else if (
			uarSource === 'annualReview' &&
			this.props.requestType !== RequestTypes.ANNUAL_REVIEW
		) {
			this.props.updateRequestType(RequestTypes.ANNUAL_REVIEW);
		}

		if (this.props.trackingNumber !== tn) {
			this.props.updateTrackingNumber(tn);
		}
		const page = query.get('page');
		this.setState({ pageLocation: page });

		if (requestType === RequestTypes.MULTI_USER) {
			esos = await getSession('UsersList').then(data =>
				data.length > 0 ? String(data).split(',') : esos
			);
		}

		if (esos && this.props.esos !== esos) {
			this.props.updateEsos(esos);
		}

		this.getAppFormDetails(tn, uarSource);
	}

	/**
	 * Resets workflow's redux store for future invocations.
	 */
	componentWillUnmount() {
		this.props.resetStore();
	}

	/**
	 * on-close handler for snackbar
	 * @param {string} reason - reason to close the snackbar
	 */
	onSnackBarCloseHandler = reason => {
		const query = new URLSearchParams(window.location.search);
		const isAnnualReviewActionFlow =
			query.get('uarSource') === 'annualReview';

		switch (reason) {
			case 'dismiss':
				this.setState({ isSnackbarOpen: false, redirectURL: '' });
				break;
			case 'timeout':
				this.setState({
					isSnackbarOpen: false,
					redirectURL: isAnnualReviewActionFlow
						? UAR_REVIEW_ROUTE
						: '/index.html',
				});
				break;
			default:
				break;
		}
	};

	/**
	 * call API to get data payload for the request
	 * @param {string} tn - tracking number
	 */
	getAppFormDetails(tn, uarSource) {
		this.props.updateLoading({
			updatedValue: true,
		});

		if (tn && tn.length >= 0) {
			axiosInstance
				.get('/api/Applications/Requests/' + tn)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								const data = response.data.payload;
								const messages = response.data.messages;
								switch (responseCode) {
									case 200:
										this.setState({ receivedApps: true });
										this.props.updateApps({
											applications: [
												...data.Applications,
											],
											rules: data.rules,
											dependencies: data.dependencyMap,
											rulesReverseLookup:
												data.rulesReverseLookup,
										});
										this.props.clearMessages();
										messages.forEach(message => {
											this.props.addMessage(message);
										});
										break;
									case 206:
										this.setState({
											receivedApps: false,
										});

										this.props.clearMessages();
										messages.forEach(message => {
											this.props.addMessage(message);
										});

										if (uarSource === 'annualReview') {
											this.setState({
												redirectURL:
													getAnnualReviewQueueProgressionPath(
														tn
													),
											});
										} else {
											this.props.updateSubmitting({
												updatedValue: true,
											});
										}

										break;
									default:
										break;
								}
							}
							break;
						default:
							this.setState({
								receivedApps: false,
							});
							break;
					}
					this.props.updateLoading({
						updatedValue: false,
					});
				})
				.catch(error => {
					/** TODO: Exception handling */
				});
		}
	}

	/**
	 * getAppComponents()
	 * loop through the GET data from /api/Applications/TN and determine the component type
	 */
	getAppComponents() {
		const appList = [];

		for (let app in this.props.appsData) {
			const newApp = this.props.appsData[app];

			let style =
				this.props.activeIndex === this.props.appsData.indexOf(newApp)
					? null
					: { display: 'none' };

			let appForm = null;

			switch (newApp.Action) {
				case 'Revoke':
					appForm = (
						<AppForm
							formId={app}
							{...this.props}
							appId={newApp.id}
						/>
					);
					break;
				case 'Add':
				case 'ModifyAccess':
				case 'Reactivate':
					switch (newApp.ComponentType) {
						case 'Empty':
						case 'Simple':
							appForm = newApp.Submitted ? (
								<Submitted />
							) : (
								<AppForm
									formId={app}
									{...this.props}
									appId={newApp.id}
								/>
							);
							break;

						default:
							appForm = (
								<Unknown
									formId={app}
									appData={newApp}
									{...this.props}
								/>
							);
							break;
					}
					break;
				default:
					break;
			}

			const form = (
				<GenericForm
					name={newApp.Name}
					style={style}
					key={newApp.id}
					appId={newApp.id}
				>
					{appForm}
				</GenericForm>
			);

			appList.push(form);
			this.props.onAppLoad({
				appData: newApp,
				trackingNumber: this.props.trackingNumber,
			});
		}

		return appList;
	}

	/**
	 * render()
	 * render the component
	 */
	render() {
		let appsToLayout = null;
		let tabbar = null;
		let esoDisplay = null;
		const isAnnualReviewActionFlow =
			new URLSearchParams(window.location.search).get('uarSource') ===
			'annualReview';
		const RequestContainer = isAnnualReviewActionFlow
			? AnnualReviewActionContainer
			: AppContainer;
		const currentApp = this.props.appsData[this.props.activeIndex];
		const uarActionPrefix =
			currentApp?.Action === 'Revoke' ? 'Revoke' : 'Modify';
		const shouldShowRedirectNotice =
			!isAnnualReviewActionFlow &&
			this.state.receivedApps === false &&
			!this.props.loading;
		let redirectNotice = shouldShowRedirectNotice ? (
				<StyleAuxiliary classes={'container'}>
					<div>
						View{' '}
						<Link
							to={getLegacyPath(
								'/RRS/RequestDetails.aspx?tn=' +
									this.props.trackingNumber +
									'&ref=wqr'
							)}
						>
							Request Details
						</Link>{' '}
						for {this.props.trackingNumber}.
					</div>
					<div>
						Make{' '}
						<Link to='/RequestFlow/RequesteeSearch'>
							another request
						</Link>
						.
					</div>
				</StyleAuxiliary>
			) : null;
		const apps = this.getAppComponents();

		if (this.state.tabApps.length > 1) {
			tabbar = (
				<Tabs
					key={'tabbar'}
					onChange={(_, newValue) => {
						if (
							newValue !== this.props.activeIndex &&
							!this.props.allForms[this.props.activeIndex].valid
						) {
							this.setState({
								isDialogOpen: true,
								activeIndexRequested: newValue,
							});
						} else {
							this.handleActiveIndexUpdate(newValue);
						}
					}}
					TabScrollButtonProps={{
						slotProps: {
							endScrollButtonIcon: {
								fontSize: 'large',
							},
							startScrollButtonIcon: {
								fontSize: 'large',
							},
						},
					}}
					value={this.props.activeIndex}
					variant='scrollable'
				>
					{this.props.appsData.map(app => {
						return (
							<Tab
								id={`app-${app.id}__tab`}
								key={app.id}
								label={app.Name}
								wrapped
							/>
						);
					})}
				</Tabs>
			);
		}

		if (this.props.esos.length > 1) {
			esoDisplay = (
				<div>
					<Headline4>Requesting for</Headline4>
					<ul>
						{this.props.esos.map((eso, index) => (
							<Chip
								key={eso.ID}
								label={eso.Name}
								trailingIcon={<Icon icon='delete' />}
								onClick={() => {
									alert(`Manager: ${eso.Manager}`);
								}}
								handleTrailingIconInteraction={() => {
									let esosCopy = [...this.props.esos];
									esosCopy.splice(index, 1);
									esosCopy = esosCopy.map(eso => eso.ID);
									this.props.updateEsos(esosCopy);
								}}
							/>
						))}
					</ul>
				</div>
			);
		}

		let snackbar = (
			<Snackbar
				action={
					<Button
						variant='text'
						onClick={() => this.onSnackBarCloseHandler('dismiss')}
					>
						Stop
					</Button>
				}
				open={this.state.isSnackbarOpen}
				message={'Cart empty. Redirecting home...'}
				autoHideDuration={4000}
				onClose={(_, reason) => this.onSnackBarCloseHandler(reason)}
			/>
		);

		let dialog = (
			<Dialog
				key={'dialog'}
				open={this.state.isDialogOpen}
				onClose={() => this.setState({ isDialogOpen: false })}
			>
				<DialogTitle>Are you sure?</DialogTitle>
				<DialogContent>
					{'The ' +
						this.props.allForms?.[this.props.activeIndex]?.name +
						' form is incomplete.'}
				</DialogContent>
				<DialogActions>
					<Button
						id='app-tab__incomplete-dialog__cancel'
						onClick={() => this.setState({ isDialogOpen: false })}
						variant='text'
					>
						Cancel
					</Button>
					<Button
						id='app-tab__incomplete-dialog__accept'
						onClick={() => {
							this.setState({ isDialogOpen: false });
							this.handleActiveIndexUpdate(
								this.state.activeIndexRequested
							);
						}}
						variant='text'
					>
						Proceed
					</Button>
				</DialogActions>
			</Dialog>
		);

		const nextQueueItem = this.state.sNextQueueItem;
		const nextQueueItemDetails = Array.isArray(nextQueueItem?.appActions)
			? nextQueueItem.appActions
					.map(appAction => {
						const actionLabel = appAction.action || 'MODIFY';
						const appName = appAction.appName || 'Unknown App';
						return `${actionLabel}: ${appName}`;
					})
					.join(' | ')
			: '';

		const uarFlowBanner = this.state.uarFlowBannerText ? (
			<Box
				key={'uar-breadcrumb'}
				sx={{
					mb: 2,
					backgroundColor: '#e6f4ff',
					color: '#0b3f66',
					border: '1px solid #b6dcff',
					borderLeft: '4px solid #1976d2',
					borderRadius: 1,
					px: 2,
					py: 1.25,
				}}
			>
				<Typography component='div' variant='body2' sx={{ fontWeight: 600 }}>
					{`${uarActionPrefix}: ${this.state.uarFlowBannerText}`}
				</Typography>
				{nextQueueItem
					? (
						<Typography component='div' variant='body2' sx={{ mt: 0.5 }}>
							Next in Queue: {nextQueueItem.staffMemberName}
							{nextQueueItemDetails
								? ` | ${nextQueueItemDetails}`
								: ''}
						</Typography>
					)
					: ''}
			</Box>
		) : null;

		const uarFlowBannerRow = uarFlowBanner ? (
			<StyleAuxiliary classes={'container'}>
				<Box
					sx={{
						mt:
							this.props.submitting ||
							/submission|submit|confirmsubmit|confirmandprocessing/i.test(
								String(this.state.pageLocation || '')
							)
								? 2
								: 1,
					}}
				>
					{uarFlowBanner}
				</Box>
			</StyleAuxiliary>
		) : null;

		appsToLayout = this.props.submitting ? (
			<RequestContainer />
		) : (
			<RequestContainer>{apps}</RequestContainer>
		);

		let children = [esoDisplay, tabbar, snackbar, dialog];

		let layout =
			this.props.appsData.length === 0 || this.props.submitting ? (
				<WithLoadingHandler>{children}</WithLoadingHandler>
			) : (
				<>{children}</>
			);

		return (
			<div>
				<div id='app-root'>
					<Fragment>
						{uarFlowBannerRow}
						{layout}
						{redirectNotice}
						{appsToLayout}
						{this.state.redirectURL && (
							<Redirect to={this.state.redirectURL} />
						)}
					</Fragment>
				</div>
				<div id='modal-root' />
			</div>
		);
	}
}

/**
 * mapStateToProps
 * map redux globally stored state to local props
 * @param {*} state
 * state:  represents the redux state
 */
const mapStateToProps = state => {
	return {
		trackingNumber: state.apps.trackingNumber,
		activeIndex: state.apps.activeIndex,
		appsData: state.apps.appsData,
		allForms: state.apps.allForms,
		esos: state.ancillary.esos,
		loading: state.apps.loading,
		submitting: state.apps.submitting,
		requestType: state.apps.requestType,
		modifyQueue: selectModifyQueue(state),
	};
};

/**
 * mapDispatchToProps
 * map action-dispatch methods to local props methods
 * @param {*} dispatch
 * dispatch:  represents the dispatch method defined in the actions
 */
const mapDispatchToProps = dispatch => {
	return {
		addMessage: message => dispatch(ancillaryActions.addMessage(message)),
		clearMessages: () => dispatch(ancillaryActions.clearMessages()),
		onAppLoad: payload => dispatch(appsActions.addApp(payload)),
		updateTrackingNumber: info =>
			dispatch(appsActions.updateTrackingNumber(info)),
		updateIndex: index => dispatch(appsActions.updateIndex(index)),
		updateApps: apps => dispatch(appsActions.updateApps(apps)),
		setAppDependencies: map =>
			dispatch(appsActions.setAppDependencies(map)),
		updateEsos: map => dispatch(ancillaryActions.updateEsos(map)),
		updateLoading: payload => dispatch(appsActions.updateLoading(payload)),
		updateRequestType: map => dispatch(appsActions.updateRequestType(map)),
		updateSubmitting: payload =>
			dispatch(appsActions.updateSubmitting(payload)),
		resetStore: () => dispatch(appsActions.resetStore())
	};
};

/**
 * Export App component
 */
export default connect(mapStateToProps, mapDispatchToProps)(AppRequest);
